import { useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { ChevronRight, BookOpen, Shield, DollarSign, TrendingUp, Crown, ArrowRight } from 'lucide-react';
import SEO from '../components/shared/SEO';

// Article data - in production this would come from a CMS/database
const ARTICLES = [
  {
    id: 'new-authority-first-90-days',
    slug: 'new-authority-first-90-days',
    title: 'What Every New Authority Must Do in the First 90 Days',
    subtitle: 'Your first 90 days will determine your first 3 years. Here\'s what serious operators focus on immediately.',
    category: 'authority-compliance',
    subcategory: 'New Authority',
    image: 'https://images.unsplash.com/photo-1586528116311-ad8dd3c8310d?w=1200&h=600&fit=crop',
    author: 'Queen B Logistics',
    date: 'February 2026',
    readTime: '10 min read',
    featured: true,
  },
  {
    id: 'why-we-call-it-premium-freight-operations',
    slug: 'why-we-call-it-premium-freight-operations',
    title: 'Why We Call It Premium Freight Operations',
    subtitle: 'We\'re not just dispatchers. What we provide goes beyond finding freight — it\'s structure, positioning, and long-term strategy.',
    category: 'queen-b-philosophy',
    subcategory: 'Our Approach',
    image: 'https://images.unsplash.com/photo-1599907450334-45fc51a51cee?w=1200&h=600&fit=crop',
    author: 'Queen B Logistics',
    date: 'February 2026',
    readTime: '7 min read',
    featured: false,
  },
  {
    id: 'when-to-add-second-truck',
    slug: 'when-to-add-second-truck',
    title: 'When to Add Your Second Truck (And When You\'re Not Ready Yet)',
    subtitle: 'Adding a second truck is not a flex — it\'s a financial decision. Learn how to know the difference.',
    category: 'scaling-fleet',
    subcategory: 'Fleet Growth',
    image: 'https://images.unsplash.com/photo-1619551734325-81aaf323686c?w=1200&h=600&fit=crop',
    author: 'Queen B Logistics',
    date: 'February 2026',
    readTime: '10 min read',
    featured: false,
  },
  {
    id: 'reduce-deadhead-without-killing-profit',
    slug: 'reduce-deadhead-without-killing-profit',
    title: 'How to Reduce Deadhead Without Killing Profit',
    subtitle: 'Deadhead isn\'t the enemy — bad strategy is. Learn the profit formula serious carriers use.',
    category: 'premium-freight-strategy',
    subcategory: 'Lane Optimization',
    image: 'https://images.unsplash.com/photo-1601584115197-04ecc0da31d7?w=1200&h=600&fit=crop',
    author: 'Queen B Logistics',
    date: 'February 2026',
    readTime: '8 min read',
    featured: false,
  },
  {
    id: 'google-workspace-for-carriers',
    slug: 'google-workspace-for-carriers',
    title: 'Why Serious Carriers Operate on Google Workspace (And Not Gmail)',
    subtitle: 'If you\'re still running your trucking business from a personal Gmail account, you\'re operating like a side hustle — not a company.',
    category: 'financial-growth',
    subcategory: 'Systems & Infrastructure',
    image: 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=1200&h=600&fit=crop',
    author: 'Queen B Logistics',
    date: 'February 2026',
    readTime: '8 min read',
    featured: false,
  },
  {
    id: 'one-financial-move-serious-carriers',
    slug: 'one-financial-move-serious-carriers',
    title: 'One Financial Move That Separates Serious Carriers From Everyone Else',
    subtitle: 'Most new carriers focus on freight rates, load boards, and miles. But the carriers who actually build long-term wealth focus on something else first.',
    category: 'financial-growth',
    subcategory: 'Systems & Infrastructure',
    image: 'https://images.unsplash.com/photo-1554224155-6726b3ff858f?w=1200&h=600&fit=crop',
    author: 'Queen B Logistics',
    date: 'February 2026',
    readTime: '6 min read',
    featured: false,
  },
];

const CATEGORIES = [
  {
    id: 'premium-freight-strategy',
    name: 'Premium Freight Strategy',
    description: 'Lane optimization, rate negotiation, and market positioning for serious operators.',
    icon: TrendingUp,
  },
  {
    id: 'authority-compliance',
    name: 'Authority & Compliance',
    description: 'DOT regulations, FMCSA requirements, and protecting your operating authority.',
    icon: Shield,
  },
  {
    id: 'financial-growth',
    name: 'Financial Growth for Carriers',
    description: 'Revenue optimization, expense management, and building real profitability.',
    icon: DollarSign,
  },
  {
    id: 'scaling-fleet',
    name: 'Scaling From 1 Truck to a Fleet',
    description: 'Systems, hiring, and infrastructure for controlled business growth.',
    icon: TrendingUp,
  },
  {
    id: 'queen-b-philosophy',
    name: 'The Queen B Philosophy',
    description: 'Our approach to premium freight operations and why quality beats quantity.',
    icon: Crown,
  },
];

const CarrierChroniclesPage = () => {
  const [activeCategory, setActiveCategory] = useState('all');
  const articlesRef = useRef(null);

  const filteredArticles = activeCategory === 'all' 
    ? ARTICLES 
    : ARTICLES.filter(a => a.category === activeCategory);

  const featuredArticle = ARTICLES.find(a => a.featured);

  const handleCategoryClick = (categoryId) => {
    const newCategory = categoryId === activeCategory ? 'all' : categoryId;
    setActiveCategory(newCategory);
    
    // Smooth scroll to articles section
    setTimeout(() => {
      articlesRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 100);
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A]" data-testid="carrier-chronicles-page">
      <SEO 
        title="The Carrier Chronicles | Strategic Insights for Freight Operators"
        description="Education. Systems. Compliance. Growth. Premium editorial content for serious owner-operators and small fleet owners."
        canonical="https://queenblogistics.com/carrier-chronicles"
        keywords="trucking industry insights, freight carrier education, owner operator tips, trucking business strategy, compliance guides"
      />

      {/* Hero Header */}
      <section className="relative py-20 md:py-28 border-b border-white/10">
        <div className="absolute inset-0 bg-gradient-to-b from-[#D4AF37]/5 to-transparent" />
        <div className="relative max-w-7xl mx-auto px-6 md:px-12 text-center">
          <div className="flex items-center justify-center gap-3 mb-6">
            <BookOpen className="w-8 h-8 text-[#D4AF37]" />
            <span className="text-[#D4AF37] text-sm uppercase tracking-[0.3em] font-medium">
              A Queen B Logistics Publication
            </span>
          </div>
          
          <h1 className="font-['Playfair_Display',serif] text-5xl md:text-7xl lg:text-8xl font-bold text-white mb-6">
            The Carrier<br />
            <span className="text-[#D4AF37] italic">Chronicles</span>
          </h1>
          
          <p className="text-white/60 text-xl md:text-2xl mb-4 font-light">
            Strategic Insights for Serious Freight Operators
          </p>
          
          <p className="text-[#D4AF37] text-sm uppercase tracking-[0.2em]">
            Education • Systems • Compliance • Growth
          </p>
          
          {/* Gold divider */}
          <div className="w-32 h-[2px] bg-gradient-to-r from-transparent via-[#D4AF37] to-transparent mx-auto mt-10" />
        </div>
      </section>

      {/* Featured Article */}
      {featuredArticle && (
        <section className="py-16 md:py-24 border-b border-white/10">
          <div className="max-w-7xl mx-auto px-6 md:px-12">
            <div className="flex items-center gap-2 mb-8">
              <div className="w-8 h-[2px] bg-[#D4AF37]" />
              <span className="text-[#D4AF37] text-sm uppercase tracking-wider">Featured</span>
            </div>
            
            <Link to={`/carrier-chronicles/${featuredArticle.slug}`} className="group block">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12">
                <div className="relative aspect-[16/10] overflow-hidden">
                  <img 
                    src={featuredArticle.image} 
                    alt={featuredArticle.title}
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                  <div className="absolute bottom-4 left-4">
                    <span className="bg-[#D4AF37] text-black px-3 py-1 text-xs font-bold uppercase tracking-wider">
                      {CATEGORIES.find(c => c.id === featuredArticle.category)?.name}
                    </span>
                  </div>
                </div>
                
                <div className="flex flex-col justify-center">
                  <p className="text-white/40 text-sm uppercase tracking-wider mb-4">
                    {featuredArticle.subcategory} • {featuredArticle.readTime}
                  </p>
                  <h2 className="font-['Playfair_Display',serif] text-3xl md:text-4xl lg:text-5xl text-white mb-6 group-hover:text-[#D4AF37] transition-colors leading-tight">
                    {featuredArticle.title}
                  </h2>
                  <p className="text-white/60 text-lg mb-6 leading-relaxed">
                    {featuredArticle.subtitle}
                  </p>
                  <div className="flex items-center gap-2 text-[#D4AF37] font-medium">
                    Read Article <ArrowRight className="w-4 h-4 group-hover:translate-x-2 transition-transform" />
                  </div>
                </div>
              </div>
            </Link>
          </div>
        </section>
      )}

      {/* Category Pillars */}
      <section className="py-16 md:py-24 border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex items-center gap-2 mb-12">
            <div className="w-8 h-[2px] bg-[#D4AF37]" />
            <span className="text-[#D4AF37] text-sm uppercase tracking-wider">Explore by Topic</span>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {CATEGORIES.map((category) => {
              const Icon = category.icon;
              const articleCount = ARTICLES.filter(a => a.category === category.id).length;
              
              return (
                <button
                  key={category.id}
                  onClick={() => handleCategoryClick(category.id)}
                  data-testid={`category-${category.id}`}
                  className={`group text-left p-6 border transition-all duration-300 ${
                    activeCategory === category.id 
                      ? 'border-[#D4AF37] bg-[#D4AF37]/5' 
                      : 'border-white/10 hover:border-white/30 bg-[#121212]'
                  }`}
                >
                  <div className="flex items-start justify-between mb-4">
                    <Icon className={`w-6 h-6 ${activeCategory === category.id ? 'text-[#D4AF37]' : 'text-white/40 group-hover:text-[#D4AF37]'} transition-colors`} />
                    <span className="text-white/30 text-sm">{articleCount} {articleCount === 1 ? 'article' : 'articles'}</span>
                  </div>
                  <h3 className={`font-['Barlow_Condensed'] text-xl font-bold uppercase mb-2 ${
                    activeCategory === category.id ? 'text-[#D4AF37]' : 'text-white'
                  }`}>
                    {category.name}
                  </h3>
                  <p className="text-white/50 text-sm leading-relaxed">
                    {category.description}
                  </p>
                </button>
              );
            })}
          </div>
          
          {activeCategory !== 'all' && (
            <button 
              onClick={() => handleCategoryClick('all')}
              className="mt-6 text-[#D4AF37] text-sm hover:underline"
            >
              ← Show all articles
            </button>
          )}
        </div>
      </section>

      {/* Articles Grid */}
      <section ref={articlesRef} className="py-16 md:py-24 scroll-mt-4">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex items-center justify-between mb-12">
            <div className="flex items-center gap-2">
              <div className="w-8 h-[2px] bg-[#D4AF37]" />
              <span className="text-[#D4AF37] text-sm uppercase tracking-wider">
                {activeCategory === 'all' ? 'All Articles' : CATEGORIES.find(c => c.id === activeCategory)?.name}
              </span>
            </div>
            <span className="text-white/40 text-sm">{filteredArticles.length} {filteredArticles.length === 1 ? 'article' : 'articles'}</span>
          </div>
          
          {filteredArticles.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {filteredArticles.map((article) => (
                <ArticleCard key={article.id} article={article} />
              ))}
            </div>
          ) : (
            <div className="text-center py-16 border border-white/10 bg-[#121212]">
              <div className="text-[#D4AF37] text-4xl mb-4">📝</div>
              <p className="text-white text-lg mb-2 font-medium">Coming Soon</p>
              <p className="text-white/50 text-sm mb-6">
                No articles in "{CATEGORIES.find(c => c.id === activeCategory)?.name}" yet.
              </p>
              <button 
                onClick={() => handleCategoryClick('all')}
                className="text-[#D4AF37] text-sm hover:underline inline-flex items-center gap-1"
              >
                ← View all articles
              </button>
            </div>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 md:py-24 bg-[#121212] border-t border-white/10">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <h2 className="font-['Playfair_Display',serif] text-3xl md:text-4xl text-white mb-6">
            Ready to Operate at a <span className="text-[#D4AF37] italic">Higher Level?</span>
          </h2>
          <p className="text-white/60 text-lg mb-8">
            Join The Hive and receive strategic dispatch management, compliance monitoring, and business infrastructure.
          </p>
          <Link 
            to="/intake"
            className="btn-primary inline-flex items-center gap-2"
          >
            Apply to Join The Hive
            <ArrowRight className="w-5 h-5" />
          </Link>
        </div>
      </section>
    </div>
  );
};

const ArticleCard = ({ article }) => {
  const category = CATEGORIES.find(c => c.id === article.category);
  
  return (
    <Link to={`/carrier-chronicles/${article.slug}`} className="group block">
      <article className="h-full flex flex-col">
        <div className="relative aspect-[16/10] overflow-hidden mb-4">
          <img 
            src={article.image} 
            alt={article.title}
            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
        </div>
        
        <div className="flex items-center gap-2 mb-3">
          <span className="text-[#D4AF37] text-xs uppercase tracking-wider font-medium">
            {category?.name}
          </span>
          <span className="text-white/30">•</span>
          <span className="text-white/40 text-xs">{article.readTime}</span>
        </div>
        
        <h3 className="font-['Playfair_Display',serif] text-xl text-white group-hover:text-[#D4AF37] transition-colors mb-3 leading-snug flex-1">
          {article.title}
        </h3>
        
        <p className="text-white/50 text-sm line-clamp-2 mb-4">
          {article.subtitle}
        </p>
        
        <div className="flex items-center gap-1 text-[#D4AF37] text-sm font-medium">
          Read <ChevronRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
        </div>
      </article>
    </Link>
  );
};

export default CarrierChroniclesPage;
